import { productsForEntitlements } from '../models/catalogue';
import { EntitlementModel } from '../models/types';
import { handleAuthRedirect } from './authRedirect';

export interface EntitlementResponse {
  tenantId: string;
  entitlements: string[];
  products?: EntitlementModel['products'];
}

/** In-flight promise cache with TTL to avoid duplicate concurrent requests. */
const CACHE_TTL_MS = 30_000;
let cachedEntry: { promise: Promise<EntitlementModel>; expiresAt: number } | null = null;

export class EntitlementService {
  static async load(): Promise<EntitlementModel> {
    const now = Date.now();
    if (cachedEntry && now < cachedEntry.expiresAt) {
      return cachedEntry.promise;
    }

    const promise = EntitlementService._fetch();
    cachedEntry = { promise, expiresAt: now + CACHE_TTL_MS };

    // On failure, evict the cache so the next call retries.
    promise.catch(() => {
      if (cachedEntry?.promise === promise) {
        cachedEntry = null;
      }
    });

    return promise;
  }

  /** Clear the in-flight cache (useful for tests or forced refresh). */
  static invalidate(): void {
    cachedEntry = null;
  }

  private static async _fetch(): Promise<EntitlementModel> {
    try {
      const response = await fetch('/api/entitlements');
      if (await handleAuthRedirect(response)) {
        throw new Error('AUTH_REDIRECT');
      }
      if (!response.ok) {
        throw new Error(`Entitlement API returned ${response.status}`);
      }

      const data = (await response.json()) as EntitlementResponse;
      const products = data.products ?? productsForEntitlements(data.entitlements);
      return {
        products,
        entitlements: data.entitlements
      };
    } catch (error) {
      throw error;
    }
  }
}
